import { Observable } from "rxjs";

import st from "lib/utils/index";
import { createAlert, removeAlert } from "modules/app/actions";

export default function createResourceInstance({
  LOAD,
  FETCH,
  FETCH_START,
  FETCH_SUCCESS,
  FETCH_FAILURE,
  stateSelector = (state) => {
    throw new Error("stateSelector parameter not defined");
  },
  stateKey = "resource",
  successAlertContent,
  withSuccessAlert = !!successAlertContent,
  withFailureAlert = false,
  getModel = (state, store, params) => Observable.of(null),
  serializeModel = (model, store, params) => model,
}) {
  const initialState = {
    model: null,
    fetchDone: false,
    fetchLoading: false,
    fetchFailed: false,
  };

  const reducersMap = {
    [LOAD || "undefined"]: (state, { model }) => {
      return {
        ...state,
        [stateKey]: {
          ...state[stateKey],
          model,
          fetchDone: true,
        },
      };
    },

    [FETCH_START]: (state) => {
      return {
        ...state,
        [stateKey]: {
          ...state[stateKey],
          fetchLoading: true,
          fetchFailed: false,
        },
      };
    },

    [FETCH_SUCCESS]: (state, { model }) => {
      return {
        ...state,
        [stateKey]: {
          ...state[stateKey],
          model,
          fetchDone: true,
          fetchLoading: false,
          fetchFailed: false,
        },
      };
    },

    [FETCH_FAILURE]: (state, { error }) => {
      return {
        ...state,
        [stateKey]: {
          ...state[stateKey],
          fetchLoading: false,
          fetchFailed: true,
        },
      };
    },
  };

  const epics = [
    (actions$, store) =>
      actions$.ofType(FETCH).switchMap(({ params }) => {
        const state = stateSelector(store.getState());
        const get$ = getModel(state, store, params).map((model) => serializeModel(model, store, params));
        return Observable.concat(
          Observable.of({ type: FETCH_START, params }),
          get$
            .map((model) => {
              return { type: FETCH_SUCCESS, model, params };
            })
            .catch((error) => {
              st.api.handleError(error, store);
              return Observable.of({ type: FETCH_FAILURE, error, params });
            })
            .takeUntil(actions$.ofType(FETCH))
        );
      }),

    ...(withSuccessAlert
      ? [
          (actions$) =>
            actions$.ofType(FETCH_START).map(({ params }) => {
              const alertUuid = `${FETCH_SUCCESS}-${params ? JSON.stringify(params) : ""}`;
              return removeAlert({ uuid: alertUuid });
            }),

          (actions$) =>
            actions$.ofType(FETCH_SUCCESS).map(({ error, params }) => {
              const alertUuid = `${FETCH_SUCCESS}-${params ? JSON.stringify(params) : ""}`;
              return createAlert({
                uuid: alertUuid,
                type: "success",
                content: successAlertContent(),
              });
            }),
        ]
      : []),

    ...(withFailureAlert
      ? [
          (actions$) =>
            actions$.ofType(FETCH_START).map(({ params }) => {
              const alertUuid = `${FETCH_FAILURE}-${params ? JSON.stringify(params) : ""}`;
              return removeAlert({ uuid: alertUuid });
            }),

          (actions$) =>
            actions$.ofType(FETCH_FAILURE).map(({ error, params }) => {
              const alertUuid = `${FETCH_FAILURE}-${params ? JSON.stringify(params) : ""}`;
              return createAlert({
                uuid: alertUuid,
                type: "error",
                content: st.api.findResponseErrorMessage(error),
                timeout: false,
              });
            }),
        ]
      : []),
  ];

  const actions = {
    load(model, params) {
      if (!LOAD) {
        throw new Error("LOAD parameter not defined");
      }
      return (dispatch, getState) => {
        dispatch({
          type: LOAD,
          model: serializeModel(model, { getState }),
          params,
        });
      };
    },

    fetch(params) {
      return { type: FETCH, params };
    },
  };

  return {
    initialState,
    reducersMap,
    actions,
    epics,
  };
}



// WEBPACK FOOTER //
// ./src/modules/utils/createResourceInstance.js